import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, HostListener } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AdvancedActionType } from '@core/enums/advanced-action-type.enum';
import { PeriodRendererParams } from '@core/typings/period-renderer.params';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { BaseComponent } from '@portal-shared/app/components/base-components/base.component';
import { ContextMenuService } from '@portal-shared/app/components/context-menu/context-menu.service';
import { ContextMenuItem } from '@portal-shared/app/components/context-menu/models/context-menu-item';
import { DraggableDirective } from '@portal-shared/app/directives/draggable.directive';
import { Period } from '@portal-shared/app/entities/period.model';
import { ResizeHelper } from '@portal-shared/app/helpers/resize.helper';
import { RouterHelper } from '@portal-shared/app/helpers/router.helper';
import { FormatNumberPipe } from '@portal-shared/app/pipes/format-number.pipe';
import { AdvancedEditBoardComponent } from '@resource-management/advanced-edit-board/advanced-edit-board.component';
import { NoteBoardComponent } from '@resource-management/note-board/note-board.component';
import { AssignmentService } from '@services/assignment.service';
import { MinimalAssignmentPeriodDto } from '@services/dtos/rows/minimal-assignment-period.dto';
import { DragDropPayload } from '@services/models/drag-drop-payload.model';
import { CellDropZoneComponent } from '@shared/components/cell-drop-zone/cell-drop-zone.component';
import { AssignmentRow } from '@shared/components/grids/rows/assignments/assignment-row';
import { AssignmentHelper } from '@shared/helpers/assignment.helper';
import { GridApi, ICellRenderer, IRowNode } from 'ag-grid-community';
import { isNil } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { ResourceAssignmentRow } from '../../rows/assignments/resource-assignment-row';
import { AssignmentPeriodCellRendererHelper } from './utils/assignment-period-cell-renderer.helper';

@Component({
  selector: 'al-assignment-period-cell-renderer',
  templateUrl: './assignment-period-cell-renderer.component.html',
  styleUrls: ['./assignment-period-cell-renderer.component.scss'],
  imports: [
    MatTooltipModule,
    TranslateModule,
    CellDropZoneComponent,
    DraggableDirective,
    FormatNumberPipe,
    FontAwesomeModule,
    AsyncPipe
  ]
})
export class AssignmentPeriodCellRendererComponent extends BaseComponent implements ICellRenderer {
  private disableCellEdit$ = new BehaviorSubject(true);
  public useDraggingAnchor$ = ResizeHelper.observeElementWidthOver(100, this.host.nativeElement);

  public row?: AssignmentRow;

  public plannedHours?: number;
  public realHours?: number;
  public hasRealHours?: boolean;
  public isReadOnly?: boolean;
  public resourceId?: string;
  public period?: Period;
  public isFirstColumn = true;
  private api?: GridApi<AssignmentRow>;
  public payload?: DragDropPayload;
  public isBeingEdited = false;
  public note?: string;
  public canBeSideEdited = false;

  private contextMenuItems: ContextMenuItem[] = [];
  private readonly defaultContextMenuItems: ContextMenuItem[] = [
    {
      titleKey: 'shared.assignmentBoard.contextMenu.insert',
      subTitleKey: 'shared.assignmentBoard.contextMenu.shiftCellsRight',

      disabled$: this.disableCellEdit$,
      click: async () => {
        await this.shift(1);
      }
    },
    {
      titleKey: 'shared.assignmentBoard.contextMenu.delete',
      subTitleKey: 'shared.assignmentBoard.contextMenu.shiftCellsLeft',

      disabled$: this.disableCellEdit$,
      click: async () => {
        await this.shift(-1);
      }
    }
  ];

  public node!: IRowNode<AssignmentRow>;
  public isProjectActive = true;

  public constructor(
    private assignmentService: AssignmentService,
    private contextMenuService: ContextMenuService,
    private host: ElementRef<HTMLDivElement>,
    private router: Router,
    private route: ActivatedRoute
  ) {
    super();
  }

  public agInit(params: PeriodRendererParams<AssignmentRow, MinimalAssignmentPeriodDto>) {
    this.api = params.api;
    this.update(params);
    this.subscribe(this.subscribeToCellSideEdited());
  }

  private async shift(direction: -1 | 1) {
    const assignmentId = this.row?.dto.assignmentId;
    const assignmentDto = this.row?.toAssignmentDto();
    if (assignmentId && !AssignmentHelper.isNewAssignmentId(assignmentId) && this.period && this.row) {
      await this.assignmentService.shiftAssignment(assignmentDto!, this.period.id, direction);
    }
  }

  public refresh(params: PeriodRendererParams<AssignmentRow, MinimalAssignmentPeriodDto>) {
    this.update(params);
    return true;
  }

  @HostListener('contextmenu', ['$event'])
  public onContextMenu(event: PointerEvent) {
    this.contextMenuService.onContextMenu(event, this.contextMenuItems);
  }

  public openContextMenu(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.contextMenuService.open(
      {
        left: event.clientX,
        top: event.clientY
      },
      this.contextMenuItems
    );
  }

  private subscribeToCellSideEdited() {
    return RouterHelper.observeQueryParams(
      this.route,
      AdvancedEditBoardComponent.assignmentIdParam,
      AdvancedEditBoardComponent.startPeriodIdParam,
      AdvancedEditBoardComponent.endPeriodIdParam,
      NoteBoardComponent.assignmentIdParam,
      NoteBoardComponent.periodIdParam
    ).subscribe(params => {
      const isAdvancedEdited =
        this.row?.dto.assignmentId === params[AdvancedEditBoardComponent.assignmentIdParam] &&
        !!this.period?.id &&
        this.period.id >= Number(params[AdvancedEditBoardComponent.startPeriodIdParam]) &&
        this.period.id <= Number(params[AdvancedEditBoardComponent.endPeriodIdParam]);
      const isNoteEdited =
        this.row?.dto.assignmentId === params[NoteBoardComponent.assignmentIdParam] &&
        this.period?.id === Number(params[NoteBoardComponent.periodIdParam]);

      this.isBeingEdited = isAdvancedEdited || isNoteEdited;
    });
  }

  public update(params: PeriodRendererParams<AssignmentRow, MinimalAssignmentPeriodDto>) {
    const [firstCol] = params.api.getDisplayedCenterColumns() ?? []; //get only visible columns in center (not pinned-left group columns)
    this.isFirstColumn = params.column === firstCol;
    this.node = params.node;
    this.row = params.data;
    this.period = params.period;
    const assignmentId = this.row?.dto.assignmentId;
    this.canBeSideEdited = !!assignmentId && !AssignmentHelper.isNewAssignmentId(assignmentId);

    if (!this.row || !params.value || !params.data) return;

    if (this.row instanceof ResourceAssignmentRow && !this.row.project.isTimeoffProject) {
      this.isProjectActive = this.row.project.isActive;
    }

    this.payload = {
      assignmentPeriod: params.value,
      assignment: params.data.toAssignmentDto()
    };

    this.resourceId = this.row?.resourceId;

    const value = params.value;
    this.plannedHours = value?.plannedHours ?? undefined;
    this.realHours = value?.realHours;
    this.hasRealHours = this.period?.hasActualHours;
    this.isReadOnly = value?.readOnly;
    this.note = value?.note;

    AssignmentPeriodCellRendererHelper.handleEditRequest(params);

    this.contextMenuItems = [
      ...this.defaultContextMenuItems,
      ...this.contextMenuService.getGridApiItems(() => this.api, this.disableCellEdit$),
      ...this.getSideEditMenuItems()
    ];
    this.disableCellEdit$.next(!this.isCellEditEnabled());
  }

  private isCellEditEnabled() {
    const assignmentId = this.row?.dto.assignmentId;
    return (
      !isNil(assignmentId) &&
      !isNil(this.period) &&
      !isNil(this.row) &&
      !AssignmentHelper.isNewAssignmentId(assignmentId) &&
      !this.period.hasActualHours &&
      !this.isReadOnly
    );
  }

  private getSideEditMenuItems(): ContextMenuItem[] {
    if (!this.canBeSideEdited) return [];

    const periodId = this.period?.id;
    return [
      {
        separator: 'above',
        titleKey: 'shared.assignmentBoard.contextMenu.advancedEdit',
        disabled$: this.disableCellEdit$,
        click: async () => {
          await this.router.navigate(['edit'], {
            relativeTo: this.route,
            queryParamsHandling: 'merge',
            queryParams: {
              [AdvancedEditBoardComponent.assignmentIdParam]: this.row?.dto.assignmentId,
              [AdvancedEditBoardComponent.startPeriodIdParam]: periodId,
              [AdvancedEditBoardComponent.endPeriodIdParam]: periodId,
              [AdvancedEditBoardComponent.actionParam]: AdvancedActionType.Repeat
            }
          });
        }
      },
      {
        titleKey: 'shared.assignmentBoard.contextMenu.viewNote',
        click: () => this.openNoteBoard()
      }
    ];
  }

  protected async openNoteBoard() {
    if (!this.canBeSideEdited) return;

    const periodId = this.period?.id;
    await this.router.navigate(['note'], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        [NoteBoardComponent.assignmentIdParam]: this.row?.dto.assignmentId,
        [NoteBoardComponent.periodIdParam]: periodId
      }
    });
  }
}
